/**
 * Ports app/static/js/theme.js's OS-preference-following behavior (the
 * legacy app never had a manual toggle - confirmed by audit), extended
 * with an explicit user override on top of it: until someone actually
 * clicks the toggle, every visitor sees exactly the same
 * follow-the-OS behavior as before. Once set, the choice is persisted in
 * localStorage and takes precedence over the OS setting (including future
 * OS-level changes) until cleared. Applies `data-theme` on <html> before
 * React mounts so there's no flash, same as the legacy script being loaded
 * early in <head>.
 *
 * Deliberately has NO Highcharts import — Highcharts is a large dependency
 * only needed on chart-bearing pages, which lazy-load it (see App.tsx's
 * React.lazy(AnalyticsPage) and components/charts/highchartsTheme.ts).
 * Importing it here would pull it into every page's initial bundle. Chart
 * pages subscribe via onThemeChange() to stay in sync without this module
 * knowing Highcharts exists.
 */
export type Theme = 'dark' | 'light'
/** 'system' means "follow the OS setting" (the only behavior that existed
 * before the toggle) - a real choice, not just the absence of one, so
 * ThemeToggle can offer "reset to system" alongside the two explicit
 * options if it ever needs to. */
export type ThemePreference = Theme | 'system'

const STORAGE_KEY = 'vision-portal-theme-preference'

type ThemeListener = (theme: Theme) => void
const listeners = new Set<ThemeListener>()

function getSystemTheme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function readStoredPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light') return stored
  } catch {
    // localStorage unavailable (private browsing, disabled storage, etc.) -
    // fall back to following the OS setting, same as before this existed.
  }
  return 'system'
}

function resolveTheme(preference: ThemePreference): Theme {
  return preference === 'system' ? getSystemTheme() : preference
}

let currentPreference: ThemePreference = readStoredPreference()

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme)
  listeners.forEach((listener) => listener(theme))
}

/** The user's stored choice - 'system' if they've never toggled it (or
 * explicitly reset it). */
export function getThemePreference(): ThemePreference {
  return currentPreference
}

/** The theme actually being rendered right now - resolves 'system' to
 * whatever the OS currently reports. */
export function getActiveTheme(): Theme {
  return resolveTheme(currentPreference)
}

/** Sets an explicit preference (persisted) or 'system' to clear the
 * override and go back to following the OS setting. */
export function setThemePreference(preference: ThemePreference): void {
  currentPreference = preference
  try {
    if (preference === 'system') window.localStorage.removeItem(STORAGE_KEY)
    else window.localStorage.setItem(STORAGE_KEY, preference)
  } catch {
    // Storage write failed (quota, private mode) - the in-memory
    // preference above still applies for the rest of this page load.
  }
  applyTheme(resolveTheme(preference))
}

/** Subscribe to theme changes; fires immediately with the current theme. */
export function onThemeChange(listener: ThemeListener): () => void {
  listeners.add(listener)
  listener(resolveTheme(currentPreference))
  return () => listeners.delete(listener)
}

applyTheme(resolveTheme(currentPreference))

window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
  // Only follow the OS's live changes while no explicit override is set -
  // once a user has picked light/dark, later OS-level changes (e.g. the
  // system's own day/night schedule) shouldn't silently override them.
  if (currentPreference === 'system') applyTheme(e.matches ? 'dark' : 'light')
})
